import React, { useEffect, useRef, useState } from 'react'
import LogoIcon from './LogoIcon'
import AvatarWithDropdown from './AvatarWithDropdown'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '@/store/store'
import { trackEvent } from '@/lib/trackEvent'
import useSaveGameData from '@/hooks/useSaveGameData'
import { Edit, RefreshCcw, Save } from 'lucide-react'
import saved from '@/assets/cloud-check.svg'
import { discordLink } from '@/constants'
import { displayTutorial } from '@/store/features/tutorialSlice'
import { axios } from '@/services/api/axiosInstance'
import { endpoints } from '@/services/api/endpoints'
import { updateTitle } from '@/store/features/gameConfigSlice'
import { toast } from 'sonner'
import { BiRedo, BiUndo } from 'react-icons/bi'
import { GrHelpBook } from 'react-icons/gr'
import useUndoRedo from '@/hooks/useUndoRedo'
import DiscordButton from './DiscordAnimatedButton'
import { MdAdd } from 'react-icons/md'
import { clearAllChat } from '@/lib/utils'
import cookies from '@/utils/cookies'

interface NavbarProps {
  showPublish?: boolean
  showDiscordIcon?: boolean
  hideLogo?: boolean
  showTitle?: boolean
  onPublish?: () => void
}

const Navbar: React.FC<NavbarProps> = ({
  showPublish,
  showDiscordIcon,
  hideLogo,
  showTitle = false,
  onPublish,
}) => {
  const { loading } = useSaveGameData()
  const userInfo = useSelector((state: RootState) => state.user)
  const gameStatus = useSelector(
    (state: RootState) => state.gameDetails.gameStatus
  )
  const publishStatus = useSelector(
    (state: RootState) => state.gameDetails.publishStatus
  )
  const reduxGameTitle = useSelector(
    (state: RootState) => state.gameConfig.title
  )
  const gameId = useSelector((state: RootState) => state.gameDetails.gameId)
  // Local state for title editing and save UI
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [editedTitle, setEditedTitle] = useState(reduxGameTitle ?? '')
  const [showSavedText, setShowSavedText] = useState(false)
  const accessToken = cookies.get('ai_tk')

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { canRedo, canUndo, undo, redo } = useUndoRedo()
  const prevLoadingRef = useRef(loading)

  useEffect(() => {
    let timer: NodeJS.Timeout

    if (prevLoadingRef.current && !loading) {
      // Saving just completed
      setShowSavedText(true)
      timer = setTimeout(() => {
        setShowSavedText(false)
      }, 3000)
    }

    prevLoadingRef.current = loading

    return () => {
      if (timer) clearTimeout(timer)
    }
  }, [loading])

  useEffect(() => {
    setEditedTitle(reduxGameTitle ?? '') // Ensure the input has the latest title
  }, [reduxGameTitle])

  const login = () => {
    trackEvent('Login Clicked', { location: 'Navbar' })
    navigate('/login')
  }

  const handleDiscordClick = () => {
    trackEvent('Discord Link Clicked', {
      location: 'Navbar',
      type: showDiscordIcon ? 'Icon' : 'Button',
    })
    window.open(discordLink, '_blank')
  }

  const handlePublish = () => {
    trackEvent('Publish Game Clicked', { location: 'Navbar', gameStatus })
    if (onPublish) onPublish()
  }

  const handleEditTitle = () => setIsEditingTitle(true)

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedTitle(e.target.value)
  }
  const handleSaveTitle = async (
    e: React.FormEvent | React.MouseEvent<HTMLFormElement>
  ) => {
    e.preventDefault()
    if (editedTitle.trim() === '') {
      return toast('Game Title Cannot Be Empty')
    }
    dispatch(updateTitle(editedTitle))
    setIsEditingTitle(false)

    try {
      await axios.patch(
        endpoints.gameUpdate.url,
        { game_title: editedTitle },
        { params: { game_id: gameId } }
      )
      trackEvent('Game Title Updated', { newTitle: editedTitle })
    } catch (e) {
      console.log(e)
    }
  }

  const renderSaveStatus = () => {
    return loading ? (
      <div className="flex items-center text-[#7B7B7B]">
        <RefreshCcw className="mr-2 size-4 animate-spin" />
        <span className="mr-1 text-sm">Saving</span>
        <div className="flex">
          <span className="dot-1">.</span>
          <span className="dot-2">.</span>
          <span className="dot-3">.</span>
        </div>
      </div>
    ) : (
      <div className="flex items-center text-white">
        <img src={saved} alt="saved icon" className="size-6" />
        {showSavedText && (
          <span className="ml-2 text-sm text-[#7B7B7B]">Saved</span>
        )}
      </div>
    )
  }

  const renderGameTitle = () => {
    if (!showTitle) {
      return null
    }
    return (
      <form
        onSubmit={handleSaveTitle}
        className="hidden flex-1 items-center gap-2 overflow-hidden truncate lg:flex"
      >
        {isEditingTitle ? (
          <input
            type="text"
            value={editedTitle}
            onChange={handleTitleChange}
            className="w-full border-b border-white bg-transparent text-white focus:outline-none"
          />
        ) : (
          <h1 className="flex items-center text-[14px] font-semibold">
            <span className="text-white">Editing:</span>
            <span className="ml-2 text-[#7B7B7B]">{reduxGameTitle}</span>
          </h1>
        )}
        <span className="flex ">
          {isEditingTitle ? (
            <Save
              size={16}
              className="cursor-pointer text-white"
              onClick={handleSaveTitle}
            />
          ) : (
            <Edit
              size={16}
              className="cursor-pointer text-white"
              onClick={handleEditTitle}
            />
          )}
        </span>
      </form>
    )
  }

  return (
    <div className="z-10 flex h-14 w-full items-center gap-4 px-2 text-sm lg:h-16 lg:px-8 lg:text-base">
      <div className="flex-1 items-center gap-2 lg:flex lg:gap-4 [&>a.active]:text-white [&>a]:hidden [&>a]:items-center [&>a]:gap-2 [&>a]:text-white/50 lg:[&>a]:flex">
        {!hideLogo && (
          <div className="relative h-12">
            <LogoIcon />
          </div>
        )}
        {reduxGameTitle && renderGameTitle()}
      </div>
      <div className="flex items-center gap-2 text-white lg:gap-4">
        {showTitle && (
          <>
            <button
              className={canUndo ? '' : 'cursor-not-allowed opacity-50'}
              onClick={canUndo ? undo : () => null}
            >
              <BiUndo className="size-4 lg:size-6" />
            </button>
            <div className="h-6 border-r border-gray-700" />
            {renderSaveStatus()}
            <div className="h-6 border-r border-gray-700" />
            <button
              className={canRedo ? '' : 'cursor-not-allowed opacity-50'}
              onClick={canRedo ? redo : () => null}
            >
              <BiRedo className="size-4 lg:size-6" />
            </button>
          </>
        )}
      </div>
      <div className="flex flex-1 items-center justify-end gap-2 lg:gap-4 ">
        <a
          href="/"
          onClick={clearAllChat}
          className="flex size-[42px] items-center justify-center rounded-full bg-white/10"
        >
          <MdAdd className="animate-mini-scale size-6 cursor-pointer text-white" />
        </a>
        <button
          className="flex size-[42px] items-center justify-center rounded-full bg-white/10"
          onClick={() => dispatch(displayTutorial())}
        >
          <GrHelpBook className="animate-mini-scale size-5 cursor-pointer bg-white/10 text-white" />
        </button>
        {showPublish && (
          <>
            {showDiscordIcon && (
              <button
                onClick={handleDiscordClick}
                className="hidden h-[42px] lg:inline-flex"
              >
                <DiscordButton />
              </button>
            )}
            <a
              href="https://play.aicade.io"
              target="_blank"
              rel="noreferrer"
              className="secondary-button hidden min-h-[42px] lg:inline-block"
            >
              Play
            </a>
            {publishStatus && gameStatus === 'published' ? (
              <button className="primary-button min-h-[42px] px-2 lg:min-w-32">
                Published
              </button>
            ) : publishStatus && gameStatus === 'save_draft' ? (
              <button
                onClick={handlePublish}
                className="primary-button min-h-[42px] px-2 lg:min-w-32"
              >
                Republish
              </button>
            ) : (
              <button
                disabled={!gameId}
                onClick={handlePublish}
                className="primary-button min-h-[42px] px-2 lg:min-w-32"
              >
                Publish
              </button>
            )}
          </>
        )}
        {userInfo?.userId && accessToken ? (
          <AvatarWithDropdown userInfo={userInfo} />
        ) : (
          <button
            className="primary-button hidden h-[42px] lg:inline-flex"
            onClick={login}
          >
            Login
          </button>
        )}
      </div>
    </div>
  )
}

export default Navbar
